import joi from 'joi';
import {
  EarnEvent,
  ParentCategoryName,
  PredictionResult,
  QualifioEngageCampaignEventPayload,
} from '../../ingestor-types';
import { EventKind, EventName, EventSource } from '../../api-client';
import { hideJoiPropertyFromDoc } from '../../openapi/hide-joi-property-from-doc';

const $tags = joi
  .array()
  .items(
    joi.object({
      name: joi.string().required().description('The name of the tag.'),
      value: joi.string().required().description('The value of the tag.'),
    }),
  )
  .optional()
  .description('The list of all tags used in the event.');

const $counters = joi
  .array()
  .items(
    joi.object({
      name: joi.string().required().description('The name of the counter.'),
      value: joi.number().required().description('The value of the counter.'),
    }),
  )
  .optional()
  .description('The list of all counters used in the event (numerical values).');

const $ecommercePayload = joi
  .object({
    amountVatExcluded: joi
      .number()
      .optional()
      .description('The purchase amount excluding applicable taxes.'),
    amountVatIncluded: joi
      .number()
      .optional()
      .description('The purchase amount including taxes (excluding shipping costs).'),
    shippingCostVatIncluded: joi
      .number()
      .optional()
      .description('The shipping costs including taxes related to the purchases.'),
    totalAmountVatIncluded: joi.number().optional().description('The total purchase amount. '),
    basket: joi
      .array()
      .items(
        joi.object({
          productName: joi.string().optional().description('Name of the product.'),
          productCode: joi.string().optional().description('Code of the product.'),
          productVatExcluded: joi.number().optional().description('Unit price excluding taxes.'),
          productVatIncluded: joi.number().optional().description('Unit price including taxes.'),
          quantity: joi.number().optional().description('Unit price excluding taxes.'),
        }),
      )
      .optional()
      .description('The details about the basket items.'),
    basketTotal: joi.number().optional().description('The number of different items purchased.'),
    quantityTotal: joi.number().optional().description('The total quantity of items purchased.'),
    couponUsed: joi
      .array()
      .items(
        joi.object({
          code: joi.string().optional().description('The coupon code used in the purchase'),
        }),
      )
      .optional()
      .description('The list of coupon codes used by the member.'),
    tags: $tags.optional(),
    referralCode: joi.string().optional().description('The referral code used in the purchase'),
  })
  .meta({ className: 'EcommerceEventPayload' });

const variableValue = joi.string().allow('');

const $campaignPayload = joi
  .object<QualifioEngageCampaignEventPayload>({
    participationId: joi.string().optional(),
    globalVariables: joi
      .array()
      .items(
        joi.object({
          key: joi.string().required(),
          value: variableValue,
        }),
      )
      .required(),
    campaignId: joi.number().optional().empty('').default(joi.ref('campaign.id')),
    campaignType: joi.string().optional().empty('').default(joi.ref('campaign.campaignType')),
    campaignChannelUuid: joi.string().optional().empty('').default(joi.ref('campaign.channelUuid')),
    campaignThemes: joi
      .array()
      .items(
        joi.object({
          code: joi.string().required(),
          description: joi.string().required(),
        }),
      )
      .optional()
      .empty('')
      .default(joi.ref('campaign.themes')),
    campaignName: joi.string().optional().empty('').default(joi.ref('campaign.name')),
    campaign: joi
      .object({
        id: joi.number().required(),
        campaignType: joi.string().required(),
        channelUuid: joi.string().required(),
        themes: joi
          .array()
          .items(
            joi.object({
              code: joi.string().required(),
              description: joi.string().required(),
            }),
          )
          .required(),
        name: joi.string().required(),
      })
      .required(),
    optins: joi
      .array()
      .items(
        joi.object({
          id: joi.number().required(),
          value: joi.alternatives().try(joi.string().valid('1', '0', ''), joi.array()),
          variable: joi.string().optional().empty(''),
          label: joi.string().optional(), //required
          categories: joi
            .array()
            .items(
              joi.object({
                name: joi.string(),
                parentName: joi
                  .string()
                  .valid(...Object.values(ParentCategoryName))
                  .optional(),
                technicalName: joi.string().optional(), // Can be put to required after releasing loyalty integrator !26
              }),
            )
            .required(),
        }),
      )
      .optional(), //not true
    score: joi
      .array()
      .items(
        joi.object({
          kind: joi.string().required(),
          value: joi.number().required(),
        }),
      )
      .optional(), //not true
    personalityTest: joi
      .array()
      .items(joi.object({ group: joi.string().required(), groupName: joi.string().required() }))
      .optional(),
    website: joi.object({
      id: joi.number().required(),
      name: joi.string().required(),
    }),
    websiteName: joi.string().optional().empty('').default(joi.ref('website.name')),
    variables: joi.array().items(
      joi.object({
        key: joi.string().required(),
        value: joi
          .alternatives()
          .try(variableValue, joi.object().pattern(joi.string(), variableValue)),
      }),
    ),
    gift: joi
      .object({
        won: joi.boolean().required(),
        wonAt: joi.string().isoDate().optional(),
        giftData: joi
          .object({
            id: joi.number().required(),
            codesListId: joi.number().optional(),
            plannedAt: joi.string().isoDate().required(),
            label: joi.string().required(),
            description: joi.string().optional(),
            picture: joi.string().optional(),
            url: joi.string().optional(),
          })
          .optional(),
      })
      .optional(),
    rewards: joi
      .array()
      .items(
        joi.object({
          codeList: joi.string().required(),
          code: joi.string().required(),
          giftId: joi.number().optional(),
        }),
      )
      .optional(),
    uniqueCode: joi.string().optional(),
    burnedCodes: joi
      .array()
      .items(joi.object({ variable: joi.string().required(), code: joi.string().required() }))
      .optional(),
  })
  .meta(hideJoiPropertyFromDoc);

const $binaryPayload = joi
  .object({
    origin: joi
      .string()
      .optional()
      .description('Where the event happens. E.g. the site where an app can be downloaded.'),
    label: joi
      .string()
      .optional()
      .description(
        'The name given to the event, understandable by all users (e.g. “download app”).',
      ),
    value: joi
      .string()
      .optional()
      .description('The technical name (value) to identify the event (e.g.”app1”).'),
    tags: $tags,
    counters: $counters,
  })
  .meta({ className: 'BinaryEventPayload' });

const $predictionPayload = joi.object({
  matchExternalId: joi.string().required(),
  leagueExternalId: joi.string().required(),
  result: joi
    .string()
    .valid(...Object.values(PredictionResult))
    .required(),
  contextId: joi.string().uuid().required(),
  gameUuid: joi.string().uuid().required(),
});

export const eventPayloadSchema = joi
  .object({
    timestamp: joi.string().isoDate().required().description('When the event occurred'),
  })
  .when('source', {
    is: EventSource.ECOMMERCE,
    then: $ecommercePayload,
  })
  .when('source', {
    is: EventSource.ADVANCED_FORMAT,
    then: $predictionPayload,
  })
  .when('source', {
    is: EventSource.QUALIFIO_ENGAGE,
    then: $campaignPayload,
  })
  .when('source', {
    is: EventSource.BINARY,
    then: $binaryPayload,
  })
  .when('source', {
    is: EventSource.REFERRAL,
    then: joi
      .when('name', {
        is: EventName.ECOMMERCE,
        then: $ecommercePayload,
      })
      .when('name', {
        is: EventName.PROFILE_CREATION,
        then: joi.object({
          referralCode: joi.string().empty('').optional(),
          referrerId: joi.string().uuid(),
          refereeId: joi.string().uuid(),
        }),
      }),
  })
  .when('source', {
    is: EventSource.QUALIFIO_LOYALTY,
    then: joi
      .when('name', {
        is: EventName.MEMBER_VISIT,
        then: joi
          .object({
            previousVisit: joi.string().isoDate().optional().allow(null),
            consecutiveDailyVisits: joi.number(),
            totalDailyVisits: joi.number(),
          })
          .meta({ className: 'MemberVisitEventPayload' }),
      })
      .when('name', {
        is: EventName.PROFILE_CREATION,
        then: joi
          .object({
            referralCode: joi.string().empty('').optional(),
          })
          .meta({ className: 'ProfileCreationEventPayload' }),
      })
      .when('name', {
        is: EventName.MEMBER_CLICK,
        then: joi
          .object({
            buttonId: joi.string(),
          })
          .meta({ className: 'MemberClickEventPayload' }),
      })
      .when('name', {
        is: EventName.BIRTHDAY,
        then: joi
          .object({
            age: joi.number(),
          })
          .meta({ className: 'BirthdayEventPayload' }),
      })
      .when('name', {
        is: EventName.LEVEL_CHANGE,
        then: joi
          .object({
            levelId: joi.string(),
          })
          .meta({ className: 'LevelChangeEventPayload' }),
      }),
  });

export const eventSchema = joi.object<EarnEvent>({
  clientEventId: joi
    .string()
    .required()
    .description(
      'The unique identifier of the event in the source database. Can be used as reconciliation key.',
    ),
  kind: joi
    .string()
    .valid(...Object.values(EventKind))
    .required(),
  source: joi
    .string()
    .valid(...Object.values(EventSource))
    .required(),
  name: joi.when('source', {
    is: EventSource.BINARY,
    then: joi.string().required(),
    otherwise: joi.string().valid(...Object.values(EventName)),
  }),
  accountId: joi
    .string()
    .required()
    .description('The unique identifier of the user account Qualifio Rewards Program.'),
  programId: joi.string().optional().description('The unique identifier of the program.'),
  profile: joi
    .object({
      kind: joi
        .string()
        .required()
        .description('The kind of the profile identifier (i.e.: `email`, `customerId`...).'),
      value: joi.string().required().description('The value of the profile identifier.'),
    })
    .required()
    .description('The list of identifiers of the member that will be impacted by the event.'),
  referrerId: joi.string().uuid().optional().meta(hideJoiPropertyFromDoc),
  payload: joi.any().required(),
});

export const documentedEventSchema = eventSchema.keys({
  kind: joi
    .string()
    .valid(EventKind.EARN)
    .required()
    .description(
      'The type of Qualifio Rewards Program event. At this time, can be only “EARN” (event attributing points to a member).',
    ),
  source: joi
    .string()
    .valid(EventSource.ECOMMERCE, EventSource.BINARY)
    .required()
    .description('The source of the event. Will be “Ecommerce” or "Binary" for generic events.'),
  name: joi
    .string()
    .valid(EventName.PURCHASE, EventName.BASIC)
    .required()
    .description('The name of the trigger of the event. Will be “Purchase” for purchases.'),
  payload: eventPayloadSchema,
});
